import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CloudIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  FolderIcon,
  DocumentIcon,
  ServerStackIcon,
  CloudArrowDownIcon,
  LinkIcon,
  ShieldCheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { dmsAPI } from '../services/api';

const DMSConfigPage = () => {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'sharepoint');
  const [testingConnection, setTestingConnection] = useState(false);

  const dmsTypes = [
    { id: 'sharepoint', name: 'SharePoint', icon: CloudIcon, color: 'blue', description: 'Microsoft SharePoint Online & On-Premises' },
    { id: 'onedrive', name: 'OneDrive', icon: CloudIcon, color: 'sky', description: 'Microsoft OneDrive for Business' },
    { id: 'google_drive', name: 'Google Drive', icon: FolderIcon, color: 'yellow', description: 'Google Workspace Drive' },
    { id: 'dropbox', name: 'Dropbox', icon: FolderIcon, color: 'blue', description: 'Dropbox Business' },
    { id: 'box', name: 'Box', icon: FolderIcon, color: 'blue', description: 'Box Enterprise Content Management' },
    { id: 'confluence', name: 'Confluence', icon: DocumentIcon, color: 'blue', description: 'Atlassian Confluence Wiki' },
    { id: 's3', name: 'AWS S3', icon: ServerStackIcon, color: 'orange', description: 'Amazon S3 Object Storage' },
    { id: 'azure_blob', name: 'Azure Blob', icon: CloudIcon, color: 'blue', description: 'Azure Blob Storage' },
    { id: 'alfresco', name: 'Alfresco', icon: FolderIcon, color: 'green', description: 'Alfresco Content Services' },
    { id: 'documentum', name: 'Documentum', icon: DocumentIcon, color: 'purple', description: 'OpenText Documentum' },
    { id: 'custom', name: 'Custom DMS', icon: Cog6ToothIcon, color: 'gray', description: 'Custom API Integration' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const validTabs = ['sharepoint', 'onedrive', 'google_drive', 'dropbox', 'box', 'confluence', 's3', 'azure_blob', 'alfresco', 'documentum', 'custom'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, statusRes] = await Promise.all([
        dmsAPI.getConfig(),
        dmsAPI.getStatus()
      ]);
      setConfig(configRes.data?.config || {});
      setStatus(statusRes.data?.status || {});
    } catch (error) {
      console.error('Error loading DMS data:', error);
      toast.error('Failed to load DMS configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Saving DMS config:', config);
      await dmsAPI.updateConfig(config);
      toast.success('DMS configuration saved');
      await loadData();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (dmsType, field, value) => {
    setConfig(prev => ({
      ...prev,
      [dmsType]: {
        ...prev[dmsType],
        [field]: value
      }
    }));
  };

  const handleSetActiveDMS = async (dmsType) => {
    try {
      setConfig(prev => ({ ...prev, active_dms: dmsType }));
      await dmsAPI.setActiveDMS(dmsType);
      toast.success(`${dmsTypes.find(d => d.id === dmsType)?.name} set as active DMS`);
      await loadData();
    } catch (error) {
      toast.error('Failed to set active DMS');
    }
  };

  const handleTestConnection = async (dmsType) => {
    try {
      setTestingConnection(true);
      const response = await dmsAPI.testConnection(dmsType);
      if (response.data?.connected) {
        toast.success(`Connection to ${dmsTypes.find(d => d.id === dmsType)?.name} successful!`);
      } else {
        toast.error(response.data?.message || `Failed to connect to ${dmsTypes.find(d => d.id === dmsType)?.name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to connect to ${dmsTypes.find(d => d.id === dmsType)?.name}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSync = async (dmsType) => {
    try {
      toast.loading(`Starting sync from ${dmsTypes.find(d => d.id === dmsType)?.name}...`);
      await dmsAPI.syncDocuments({ dms: dmsType });
      toast.dismiss();
      toast.success('Sync started successfully');
      await loadData();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Sync failed');
    }
  };

  const renderSharePointConfig = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-800 mb-2">SharePoint Integration</h4>
        <p className="text-sm text-blue-600">
          Connect to SharePoint Online or On-Premises to sync documents and content for RAG indexing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tenant ID</label>
          <input
            type="text"
            value={config.sharepoint?.tenant_id || ''}
            onChange={(e) => handleConfigChange('sharepoint', 'tenant_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
          <input
            type="text"
            value={config.sharepoint?.client_id || ''}
            onChange={(e) => handleConfigChange('sharepoint', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="App Registration Client ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
          <input
            type="password"
            value={config.sharepoint?.client_secret || ''}
            onChange={(e) => handleConfigChange('sharepoint', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Site URL</label>
          <input
            type="url"
            value={config.sharepoint?.site_url || ''}
            onChange={(e) => handleConfigChange('sharepoint', 'site_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://yourtenant.sharepoint.com/sites/yoursite"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Document Library</label>
          <input
            type="text"
            value={config.sharepoint?.document_library || ''}
            onChange={(e) => handleConfigChange('sharepoint', 'document_library', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Documents"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sync Interval (seconds)</label>
          <input
            type="number"
            value={config.sharepoint?.sync_interval || 3600}
            onChange={(e) => handleConfigChange('sharepoint', 'sync_interval', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            min="300"
            step="300"
          />
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="sharepoint_sync_enabled"
          checked={config.sharepoint?.sync_enabled || false}
          onChange={(e) => handleConfigChange('sharepoint', 'sync_enabled', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="sharepoint_sync_enabled" className="text-sm font-medium text-gray-700">
          Enable automatic sync
        </label>
      </div>
    </div>
  );

  const renderOneDriveConfig = () => (
    <div className="space-y-6">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-sky-800 mb-2">OneDrive for Business Integration</h4>
        <p className="text-sm text-sky-600">
          Connect to OneDrive for Business to sync files and folders for RAG indexing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
          <input
            type="text"
            value={config.onedrive?.client_id || ''}
            onChange={(e) => handleConfigChange('onedrive', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sky-500 focus:border-sky-500"
            placeholder="App Registration Client ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
          <input
            type="password"
            value={config.onedrive?.client_secret || ''}
            onChange={(e) => handleConfigChange('onedrive', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sky-500 focus:border-sky-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Redirect URI</label>
          <input
            type="url"
            value={config.onedrive?.redirect_uri || ''}
            onChange={(e) => handleConfigChange('onedrive', 'redirect_uri', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-sky-500 focus:border-sky-500"
            placeholder="https://yourapp.com/auth/callback"
          />
        </div>
      </div>
    </div>
  );

  const renderGoogleDriveConfig = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-yellow-800 mb-2">Google Drive Integration</h4>
        <p className="text-sm text-yellow-600">
          Connect to Google Workspace Drive using a service account for document sync.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Service Account JSON</label>
          <textarea
            value={config.google_drive?.service_account_json || ''}
            onChange={(e) => handleConfigChange('google_drive', 'service_account_json', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500 font-mono text-sm"
            rows={6}
            placeholder='{"type": "service_account", "project_id": "...", ...}'
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Root Folder ID</label>
          <input
            type="text"
            value={config.google_drive?.folder_id || ''}
            onChange={(e) => handleConfigChange('google_drive', 'folder_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
            placeholder="1a2b3c4d5e6f7g8h9i0j"
          />
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="google_drive_sync_enabled"
          checked={config.google_drive?.sync_enabled || false}
          onChange={(e) => handleConfigChange('google_drive', 'sync_enabled', e.target.checked)}
          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
        />
        <label htmlFor="google_drive_sync_enabled" className="text-sm font-medium text-gray-700">
          Enable automatic sync
        </label>
      </div>
    </div>
  );

  const renderDropboxConfig = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-800 mb-2">Dropbox Business Integration</h4>
        <p className="text-sm text-blue-600">
          Connect to Dropbox Business to sync files for RAG indexing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">App Key</label>
          <input
            type="text"
            value={config.dropbox?.app_key || ''}
            onChange={(e) => handleConfigChange('dropbox', 'app_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Your Dropbox App Key"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">App Secret</label>
          <input
            type="password"
            value={config.dropbox?.app_secret || ''}
            onChange={(e) => handleConfigChange('dropbox', 'app_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Access Token</label>
          <input
            type="password"
            value={config.dropbox?.access_token || ''}
            onChange={(e) => handleConfigChange('dropbox', 'access_token', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Root Folder</label>
          <input
            type="text"
            value={config.dropbox?.root_folder || ''}
            onChange={(e) => handleConfigChange('dropbox', 'root_folder', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="/Company Documents"
          />
        </div>
      </div>
    </div>
  );

  const renderS3Config = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-orange-800 mb-2">AWS S3 Integration</h4>
        <p className="text-sm text-orange-600">
          Connect to Amazon S3 buckets to sync objects for RAG indexing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Access Key ID</label>
          <input
            type="text"
            value={config.s3?.access_key_id || ''}
            onChange={(e) => handleConfigChange('s3', 'access_key_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="AKIAIOSFODNN7EXAMPLE"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secret Access Key</label>
          <input
            type="password"
            value={config.s3?.secret_access_key || ''}
            onChange={(e) => handleConfigChange('s3', 'secret_access_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
          <select
            value={config.s3?.region || 'us-east-1'}
            onChange={(e) => handleConfigChange('s3', 'region', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="us-east-1">US East (N. Virginia)</option>
            <option value="us-east-2">US East (Ohio)</option>
            <option value="us-west-1">US West (N. California)</option>
            <option value="us-west-2">US West (Oregon)</option>
            <option value="eu-west-1">EU (Ireland)</option>
            <option value="eu-west-2">EU (London)</option>
            <option value="eu-central-1">EU (Frankfurt)</option>
            <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
            <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
            <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bucket Name</label>
          <input
            type="text"
            value={config.s3?.bucket_name || ''}
            onChange={(e) => handleConfigChange('s3', 'bucket_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="my-documents-bucket"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Prefix (Optional)</label>
          <input
            type="text"
            value={config.s3?.prefix || ''}
            onChange={(e) => handleConfigChange('s3', 'prefix', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            placeholder="documents/"
          />
        </div>
      </div>
    </div>
  );

  const renderConfluenceConfig = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-800 mb-2">Atlassian Confluence Integration</h4>
        <p className="text-sm text-blue-600">
          Connect to Confluence to sync wiki pages and documentation for RAG indexing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Confluence Base URL</label>
          <input
            type="url"
            value={config.confluence?.base_url || ''}
            onChange={(e) => handleConfigChange('confluence', 'base_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://yourcompany.atlassian.net/wiki"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Username (Email)</label>
          <input
            type="email"
            value={config.confluence?.username || ''}
            onChange={(e) => handleConfigChange('confluence', 'username', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="user@company.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">API Token</label>
          <input
            type="password"
            value={config.confluence?.api_token || ''}
            onChange={(e) => handleConfigChange('confluence', 'api_token', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="••••••••••••••••"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Space Keys (comma-separated)</label>
          <input
            type="text"
            value={Array.isArray(config.confluence?.space_keys) ? config.confluence.space_keys.join(', ') : ''}
            onChange={(e) => handleConfigChange('confluence', 'space_keys', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="DEV, DOCS, KB"
          />
        </div>
      </div>
    </div>
  );

  const renderGenericConfig = (dmsType) => {
    const dms = dmsTypes.find(d => d.id === dmsType);
    return (
      <div className="space-y-6">
        <div className={`bg-${dms?.color || 'gray'}-50 border border-${dms?.color || 'gray'}-200 rounded-lg p-4 mb-6`}>
          <h4 className={`font-medium text-${dms?.color || 'gray'}-800 mb-2`}>{dms?.name} Integration</h4>
          <p className={`text-sm text-${dms?.color || 'gray'}-600`}>{dms?.description}</p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Configuration Coming Soon</h4>
          <p className="text-sm text-gray-600">
            The {dms?.name} integration is currently being developed. Please check back later or contact support for early access.
          </p>
        </div>
      </div>
    );
  };

  const renderConfigForm = () => {
    switch (activeTab) {
      case 'sharepoint':
        return renderSharePointConfig();
      case 'onedrive':
        return renderOneDriveConfig();
      case 'google_drive':
        return renderGoogleDriveConfig();
      case 'dropbox':
        return renderDropboxConfig();
      case 's3':
        return renderS3Config();
      case 'confluence':
        return renderConfluenceConfig();
      default:
        return renderGenericConfig(activeTab);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">DMS Integration</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Connect to Document Management Systems to sync and index documents for RAG.
        </p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <ServerStackIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Connected Systems</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Object.values(status).filter(s => s.connected).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <DocumentIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Documents Indexed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Object.values(status).reduce((acc, s) => acc + (s.documents_indexed || s.pages_indexed || s.objects_indexed || s.blobs_indexed || s.items_indexed || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <CloudArrowDownIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active DMS</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {dmsTypes.find(d => d.id === config.active_dms)?.name || 'None'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last Sync</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {status[config.active_dms]?.last_sync ? new Date(status[config.active_dms].last_sync).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* DMS Type Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Document Systems</h3>
            <nav className="space-y-1">
              {dmsTypes.map((dms) => (
                <button
                  key={dms.id}
                  onClick={() => setActiveTab(dms.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === dms.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  <dms.icon className="h-5 w-5 mr-3" />
                  {dms.name}
                  {status[dms.id]?.connected && (
                    <CheckCircleIcon className="h-4 w-4 ml-auto text-green-500" />
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Configuration Form */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            {/* Active DMS Badge */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {(() => {
                  const activeDMS = dmsTypes.find(d => d.id === activeTab);
                  const Icon = activeDMS?.icon || CloudIcon;
                  return <Icon className="h-8 w-8 text-blue-600" />;
                })()}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {dmsTypes.find(d => d.id === activeTab)?.name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {dmsTypes.find(d => d.id === activeTab)?.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {config.active_dms === activeTab ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <button
                    onClick={() => handleSetActiveDMS(activeTab)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-full transition-colors"
                  >
                    Set as Active
                  </button>
                )}
              </div>
            </div>

            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mb-6">
              <div className="flex items-center space-x-3">
                <ShieldCheckIcon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-700 dark:text-gray-300">Enable Integration</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config[activeTab]?.enabled || false}
                  onChange={(e) => handleConfigChange(activeTab, 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Config Form */}
            {renderConfigForm()}

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex space-x-3">
                <button
                  onClick={() => handleTestConnection(activeTab)}
                  disabled={testingConnection || !config[activeTab]?.enabled}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testingConnection ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleSync(activeTab)}
                  disabled={!config[activeTab]?.enabled || !status[activeTab]?.connected}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Sync Now
                </button>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DMSConfigPage;
